import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "urql"
import { toast } from "sonner"
import { usePostHog } from "@posthog/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { apiKeySchema, type ApiKeyFormValues } from "@/lib/schemas"
import { CreateApiKeyMutation } from "@/lib/graphql/mutations"

interface ApiKeyFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (rawKey: string) => void
}

export function ApiKeyForm({ open, onOpenChange, onSuccess }: ApiKeyFormProps) {
  const posthog = usePostHog()
  const [, createApiKey] = useMutation(CreateApiKeyMutation)

  const form = useForm<ApiKeyFormValues>({
    resolver: zodResolver(apiKeySchema) as Resolver<ApiKeyFormValues>,
    defaultValues: {
      name: "",
      scopes: "",
      sourceIds: "",
      expiresAt: "",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({ name: "", scopes: "", sourceIds: "", expiresAt: "" })
    }
  }, [open, form])

  const onSubmit = async (values: ApiKeyFormValues) => {
    const scopes = values.scopes
      ? values.scopes.split(",").map((s) => s.trim()).filter(Boolean)
      : undefined

    const sourceIds = values.sourceIds
      ? values.sourceIds.split(",").map((s) => s.trim()).filter(Boolean)
      : undefined

    const expiresAt = values.expiresAt
      ? new Date(values.expiresAt).toISOString()
      : undefined

    const result = await createApiKey({
      input: { name: values.name, scopes, sourceIds, expiresAt },
    })

    if (result.error) {
      toast.error(result.error.message)
      return
    }

    const rawKey = result.data?.createAPIKey.rawKey
    if (!rawKey) {
      toast.error("API key created but no key returned")
      return
    }
    posthog?.capture("api_key_created", { has_expiry: !!values.expiresAt, has_scopes: !!values.scopes })
    onSuccess(rawKey)
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="My API Key" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="scopes">Scopes</Label>
              <Input
                id="scopes"
                placeholder="read, write (comma-separated)"
                {...form.register("scopes")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sourceIds">Source IDs</Label>
              <Input
                id="sourceIds"
                className="font-mono text-xs"
                placeholder="UUID, UUID (comma-separated, optional)"
                {...form.register("sourceIds")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiresAt">Expires At</Label>
              <Input
                id="expiresAt"
                type="datetime-local"
                {...form.register("expiresAt")}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
